import { http } from "@/api/http";
import type { OkResponse } from "@/types/api";
import type { LoginTotpRequest, TwoFactorChallengeResponse } from "@/types/accountSecurity";
import { isTwoFactorChallenge } from "@/types/accountSecurity";
import type { LoginCredentials, RegistrationDetails, User } from "@/types/user";

interface AuthResponse {
  user: User;
}

export type LoginResult = User | TwoFactorChallengeResponse;

interface PasswordResetRequestResponse extends OkResponse {
  message: string;
}

export interface LoginCapabilities {
  google_login_enabled: boolean;
}

const MICROSOFT_ACCOUNT_LOGIN_PATH = "/api/auth/oauth/microsoft/start";
const GOOGLE_ACCOUNT_LOGIN_PATH = "/api/auth/oauth/google/start";

export function beginGoogleAccountLogin(): void {
  window.location.assign(GOOGLE_ACCOUNT_LOGIN_PATH);
}

export function beginMicrosoftAccountLogin(): void {
  window.location.assign(MICROSOFT_ACCOUNT_LOGIN_PATH);
}

export async function getLoginCapabilities(): Promise<LoginCapabilities> {
  const response = await http.get<LoginCapabilities>("/auth/login-capabilities");
  return response.data;
}

export async function getCurrentUser(): Promise<User> {
  const response = await http.get<AuthResponse>("/auth/me");
  return response.data.user;
}

export async function login(credentials: LoginCredentials): Promise<LoginResult> {
  const response = await http.post<AuthResponse | TwoFactorChallengeResponse>(
    "/auth/login",
    credentials,
  );
  if (isTwoFactorChallenge(response.data)) return response.data;
  return response.data.user;
}

export async function completeLoginTotp(request: LoginTotpRequest): Promise<User> {
  const response = await http.post<AuthResponse>("/auth/login/totp", request);
  return response.data.user;
}

export async function register(details: RegistrationDetails): Promise<User> {
  const response = await http.post<AuthResponse>("/auth/register", details);
  return response.data.user;
}

export async function requestPasswordReset(
  identifier: string,
): Promise<PasswordResetRequestResponse> {
  const response = await http.post<PasswordResetRequestResponse>("/auth/forgot-password", {
    identifier,
  });
  return response.data;
}

export async function resetPassword(token: string, password: string): Promise<OkResponse> {
  const response = await http.post<OkResponse>("/auth/reset-password", { password, token });
  return response.data;
}

export async function logout(): Promise<OkResponse> {
  const response = await http.post<OkResponse>("/auth/logout", {});
  return response.data;
}
